import { useEffect, useState } from 'react'
import { Plus, Pencil, Trash2, Loader2, Mail } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../lib/shadcn/alert-dialog'
import { toast } from '../../lib/shadcn/sonner'
import {
  useGetEmailTemplates,
  useUpsertEmailTemplate,
  useDeleteEmailTemplate,
  useUploadEmailImage,
} from '../../hooks/backend/crm'
import RichTextEditor from '../../components/RichTextEditor'
import AiEmailAssistant from '../../components/AiEmailAssistant'

interface EmailTemplate {
  id: number
  name: string
  subject: string
  body: string
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      const comma = result.indexOf(',')
      resolve(comma >= 0 ? result.slice(comma + 1) : result)
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

export default function EmailTemplatesSettings() {
  const getTemplates = useGetEmailTemplates()
  const upsertTemplate = useUpsertEmailTemplate()
  const deleteTemplate = useDeleteEmailTemplate()
  const uploadEmailImage = useUploadEmailImage()

  const templates = (getTemplates.data as EmailTemplate[] | undefined) ?? []

  const [editing, setEditing] = useState<EmailTemplate | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [name, setName] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [toDelete, setToDelete] = useState<EmailTemplate | null>(null)

  useEffect(() => {
    void getTemplates.trigger({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const refresh = () => void getTemplates.trigger({}, { skipCache: true })

  const openNew = () => {
    setEditing(null)
    setName('')
    setSubject('')
    setBody('')
    setDialogOpen(true)
  }

  const openEdit = (t: EmailTemplate) => {
    setEditing(t)
    setName(t.name)
    setSubject(t.subject ?? '')
    setBody(t.body ?? '')
    setDialogOpen(true)
  }

  const handleImageUpload = async (file: File): Promise<string | null> => {
    try {
      const base64 = await fileToBase64(file)
      const res = (await uploadEmailImage.trigger({
        fileName: file.name,
        data: base64,
        mimeType: file.type,
      })) as { url?: string } | undefined
      if (!res?.url) {
        toast.error('Image upload failed')
        return null
      }
      return res.url
    } catch {
      toast.error('Image upload failed')
      return null
    }
  }

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Template name is required')
      return
    }
    try {
      await upsertTemplate.trigger({
        ...(editing ? { id: editing.id } : {}),
        name: name.trim(),
        subject,
        body,
      })
      toast.success(editing ? 'Template updated' : 'Template created')
      setDialogOpen(false)
      refresh()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save template')
    }
  }

  const handleDelete = async () => {
    if (!toDelete) return
    try {
      await deleteTemplate.trigger({ id: toDelete.id })
      toast.success('Template deleted')
      setToDelete(null)
      refresh()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete template')
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle>Email Templates</CardTitle>
            <CardDescription>
              Create reusable email templates with rich text and images. Pick one when composing an email.
            </CardDescription>
          </div>
          <Button size="sm" onClick={openNew} className="gap-1.5 shrink-0">
            <Plus className="h-4 w-4" /> New Template
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {getTemplates.loading && templates.length === 0 ? (
          <div className="flex items-center justify-center py-6 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : templates.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">No templates yet. Create your first one.</p>
        ) : (
          <div className="space-y-2">
            {templates.map(t => (
              <div
                key={t.id}
                className="flex items-center gap-3 rounded-md border border-border p-3"
              >
                <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-foreground truncate">{t.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {t.subject || <span className="italic">No subject</span>}
                  </p>
                </div>
                <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => openEdit(t)}>
                  <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-muted-foreground hover:text-destructive"
                  aria-label={`Delete ${t.name}`}
                  onClick={() => setToDelete(t)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={o => { if (!upsertTemplate.loading) setDialogOpen(o) }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Template' : 'New Template'}</DialogTitle>
            <DialogDescription>
              Templates can be selected when composing an email to pre-fill the subject and body.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="tpl-name">Template Name <span className="text-destructive">*</span></Label>
              <Input id="tpl-name" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Welcome email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tpl-subject">Subject</Label>
              <Input id="tpl-subject" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Email subject" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label>Body</Label>
                <AiEmailAssistant
                  existingBody={body}
                  onGenerated={r => {
                    if (r.subject && !subject.trim()) setSubject(r.subject)
                    setBody(r.body)
                  }}
                />
              </div>
              <RichTextEditor
                value={body}
                onChange={setBody}
                onImageUpload={handleImageUpload}
                placeholder="Write your template…"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={upsertTemplate.loading}>
              Cancel
            </Button>
            <Button onClick={() => void handleSave()} disabled={upsertTemplate.loading}>
              {upsertTemplate.loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              {editing ? 'Save Changes' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={toDelete !== null} onOpenChange={o => { if (!o) setToDelete(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template “{toDelete?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the template. Emails already sent are unaffected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteTemplate.loading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={e => { e.preventDefault(); void handleDelete() }}
              disabled={deleteTemplate.loading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteTemplate.loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
